import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CloudFunctionClientContext from '../../../../contexts/CloudFunctionClientContext';
import DatasetDefinitionsContext from '../../../../contexts/DatasetDefinitionsContext';
import BonusPeriodsContext from '../../../../contexts/BonusPeriodsContext';
import resolveDriverRewardOutput from '../../utils/resolveDriverRewardOutput';
import getRewardOverrideOptions from '../../api/getRewardOverrideOptions';
import isDefined from '../../../../isDefined';
import getDriverOverrides from '../../api/getDriverOverrides';
import putBonusPreview from '../../api/putBonusPreview';
import buildOverrideState, {
  hasAnyOverride,
  toOverridesRequest,
} from '../../utils/buildOverrideState';
import putDriverOverrides from '../../api/putDriverOverrides';
import deleteDriverOverrides from '../../api/deleteDriverOverrides';
import getIdentifier from '../../../../getIdentifier';
import ToastContext from '../../../../contexts/ToastContext';
import { useSearchParams } from 'react-router-dom';
import useCurrentAccountId from 'hooks/useCurrentAccountId';

export type AdditionalPaymentsView = 'add' | 'manage';

interface BonusOverviewMode {
  mode: 'default' | 'override' | 'recordTagging' | 'additionalPayments';
  // Which recognition payments screen the slideout is showing.
  additionalPaymentsView?: AdditionalPaymentsView;
  // The reward-output field the exclude view was opened against. May be a
  // criteria, qualifier or disqualifier — see fieldType.
  taggableField?: BonusRecordTagging.TaggableFieldOutput;
  fieldType?: BonusRecordTagging.FieldType;
}

type RewardOverrideOptionsLookup = Map<
  string,
  BonusTypes.RewardOverrideOptions
>;

type BonusOverviewContextType =
  | {
      isLoading: boolean;
      isLoadingPreview: boolean;
      bonusOverview: undefined;
      // Optimistic count of records tagged per exclusion field, so the
      // expectations list reflects a just-saved tag before the delayed backend
      // reindex catches up.
      fieldTagCounts: Record<string, number>;
      setFieldTagCount: (fieldKey: string, count: number) => void;
      currentMode: BonusOverviewMode;
      rewardOverrideOptions: RewardOverrideOptionsLookup | undefined;
      enterOverrideMode: () => void;
      enterRecordTaggingMode: (
        taggableField: BonusRecordTagging.TaggableFieldOutput,
        fieldType: BonusRecordTagging.FieldType,
      ) => void;
      exitRecordTaggingMode: () => void;
      exitOverrideMode: () => void;
      onOverridesConfirmed: (
        note: string,
        reasonCodes: string[],
      ) => Promise<void>;
      // Reloads the reward record (and the host board, via onOverridesSaved)
      // after a change made outside the override flow, e.g. a recognition
      // payment.
      refreshBonusOverview: () => void;
      enterAdditionalPaymentsMode: (view: AdditionalPaymentsView) => void;
      exitAdditionalPaymentsMode: () => void;
      // The program being shown, known before its record loads ('' when no
      // provider is mounted).
      bonusProgramId: string;
    }
  | {
      isLoading: boolean;
      isLoadingPreview: boolean;
      bonusOverview: BonusTypes.BonusOverview;
      fieldTagCounts: Record<string, number>;
      setFieldTagCount: (fieldKey: string, count: number) => void;
      currentMode: BonusOverviewMode;
      rewardOverrideOptions: RewardOverrideOptionsLookup | undefined;
      enterOverrideMode: () => void;
      exitOverrideMode: () => void;
      enterRecordTaggingMode: (
        taggableField: BonusRecordTagging.TaggableFieldOutput,
        fieldType: BonusRecordTagging.FieldType,
      ) => void;
      exitRecordTaggingMode: () => void;
      onOverridesConfirmed: (
        note: string,
        reasonCodes: string[],
      ) => Promise<void>;
      // Reloads the reward record (and the host board, via onOverridesSaved)
      // after a change made outside the override flow, e.g. a recognition
      // payment.
      refreshBonusOverview: () => void;
      enterAdditionalPaymentsMode: (view: AdditionalPaymentsView) => void;
      exitAdditionalPaymentsMode: () => void;
      // The program being shown, known before its record loads ('' when no
      // provider is mounted).
      bonusProgramId: string;
    };

export const BonusOverviewContext =
  React.createContext<BonusOverviewContextType>({
    isLoading: true,
    isLoadingPreview: true,
    bonusOverview: undefined,
    fieldTagCounts: {},
    setFieldTagCount: window.tokenFunction,
    currentMode: { mode: 'default' },
    rewardOverrideOptions: undefined,
    enterOverrideMode: window.tokenFunction,
    exitOverrideMode: window.tokenFunction,
    enterRecordTaggingMode: window.tokenFunction,
    exitRecordTaggingMode: window.tokenFunction,
    onOverridesConfirmed: () => Promise.resolve(),
    refreshBonusOverview: window.tokenFunction,
    enterAdditionalPaymentsMode: window.tokenFunction,
    exitAdditionalPaymentsMode: window.tokenFunction,
    bonusProgramId: '',
  });

const BonusOverviewProvider = ({
  children,
  driver,
  bonusProgramId,
  variant,
  onOverridesSaved,
  onRewardsUnavailable,
}: {
  children: JSX.Element | JSX.Element[];
  driver: string;
  bonusProgramId: string;
  variant: 'slideout' | 'overview';
  // Fired after a successful override save. The slideout passes the board's
  // refresh so the grid reflects the new bonus values (the entity-app popup
  // flow instead refreshed on close).
  onOverridesSaved?: () => void;
  // Fired when the driver has no reward record for the selected period (the
  // rewards request 400s / returns nothing) — the slideout uses this to deselect
  // the driver and toast, instead of sitting stuck on spinners.
  onRewardsUnavailable?: () => void;
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { showToast } = useContext(ToastContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const { selectedBonusPeriod: bonusPeriod, selectedSubPeriod } =
    useContext(BonusPeriodsContext);
  const accountId = useCurrentAccountId();
  const [refreshKey, setRefreshKey] = useState<string>(getIdentifier());
  const [currentMode, setCurrentMode] = React.useState<BonusOverviewMode>(
    () => {
      const searchParam = searchParams.get('overrideMode');
      if (searchParam && searchParam === 'true') {
        return { mode: 'override' };
      }
      return { mode: 'default' };
    },
  );

  React.useEffect(() => {
    if (searchParams.get('overrideMode') === 'true') {
      setSearchParams((current) => {
        current.delete('overrideMode');
        return current;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [actualRewardOutput, setActualRewardOutput] = React.useState<
    BonusTypes.Rewards.AnyDriverRewardsOutput | undefined
  >();
  const [previewRewardOutput, setPreviewRewardOutput] = React.useState<
    BonusTypes.Rewards.AnyDriverRewardsOutput | undefined
  >();
  const [overrides, setOverrides] = React.useState<
    BonusTypes.Overrides.Persisted | undefined
  >();

  const [stagedOverrides, setStagedOverrides] =
    React.useState<BonusTypes.Overrides.UserInput>();

  const [rewardOverrideOptions, setRewardOverrideOptions] =
    useState<RewardOverrideOptionsLookup>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);

  const bonusProgram = useMemo<BonusTypes.BonusProgram | undefined>(() => {
    return bonusPrograms.find((b) => b.id === bonusProgramId);
  }, [bonusProgramId, bonusPrograms]);

  // If we have a preview, we use it, otherwise the actual. Reward-level overrides are now
  // projected server-side via PUT /preview (same as criteria/qualifier), so no client overlay.
  const rewardOutput = useMemo(() => {
    return previewRewardOutput ?? actualRewardOutput;
  }, [actualRewardOutput, previewRewardOutput]);

  const rewardsWithCriteriaLookup = useMemo(():
    | BonusTypes.RewardsWithCriteriaLookup
    | undefined => {
    if (!rewardOutput) {
      return undefined;
    }

    const lookup: BonusTypes.RewardsWithCriteriaLookup = {};
    rewardOutput.rewards.forEach((reward) => {
      lookup[reward.name] = {
        reward,
        criteria: rewardOutput.criteria.filter((c) => c.reward === reward.name),
      };
    });

    return lookup;
  }, [rewardOutput]);

  const stagedOverridePayChange = useMemo(() => {
    if (!actualRewardOutput || !previewRewardOutput) {
      return undefined;
    }

    return {
      actualPay: actualRewardOutput.totalReward,
      previewPay: previewRewardOutput.totalReward,
    };
  }, [actualRewardOutput, previewRewardOutput]);

  const overridesNote = useMemo(() => {
    if (!overrides) {
      return undefined;
    }

    if (overrides.comment && overrides.comment !== '') {
      return overrides.comment;
    }

    // Compatibility for records written before the unified override API:
    // legacy criteria/qualifier/value/reward overrides stored notes per entry,
    // while the unified API stores one top-level comment for the whole override
    // state. When editing one of those older records, carry forward the latest
    // nested note so the full-replace PUT does not erase the visible comment.
    const notes = [
      ...overrides.criteriaOverrides.map((o) => o.note),
      overrides.qualifierOverride?.note,
      ...overrides.valueOverrides.map((o) => o.note),
      ...(overrides.rewardOverrides ?? []).map((o) => o.note),
      ...(actualRewardOutput?.rewardOverrides ?? []).map((o) => o.note),
    ].filter(
      (
        note,
      ): note is { msg?: string; updatedBy: string; updatedOn: string } => {
        return !!note?.msg && note.msg !== '';
      },
    );

    return notes.sort((a, b) => {
      return Date.parse(b.updatedOn) - Date.parse(a.updatedOn);
    })[0]?.msg;
  }, [actualRewardOutput, overrides]);

  // The slideout renders the full override workflow in place (the dedicated
  // in-slideout override view), which should mirror the entity-app overriding
  // screen — i.e. expose the same 'overview' controls (e.g. the remove-override
  // badge button, expanded layouts). The compact 'slideout' variant is only for
  // the passive bonus sections, which never render at the same time as the
  // override view (sections show in default mode, the override body in override
  // mode). So while overriding in the slideout, present as 'overview'.
  const effectiveVariant: 'slideout' | 'overview' =
    variant === 'slideout' && currentMode.mode === 'override'
      ? 'overview'
      : variant;

  const bonusOverview = useMemo((): BonusTypes.BonusOverview | undefined => {
    if (
      isLoading ||
      !bonusProgram ||
      !rewardOutput ||
      !actualRewardOutput ||
      !bonusPeriod ||
      !rewardsWithCriteriaLookup
    ) {
      return undefined;
    }

    return {
      bonusProgram,
      bonusPeriod,
      rewardOutput,
      actualRewardOutput,
      overrides,
      overridesNote,
      stagedOverrides,
      setStagedOverrides,
      rewardsWithCriteriaLookup,
      rewardsWithCriteria: Object.values(rewardsWithCriteriaLookup).filter(
        isDefined,
      ),
      stagedOverridePayChange,
      variant: effectiveVariant,
      driver,
    };
  }, [
    isLoading,
    bonusProgram,
    rewardOutput,
    actualRewardOutput,
    bonusPeriod,
    rewardsWithCriteriaLookup,
    overrides,
    overridesNote,
    stagedOverrides,
    stagedOverridePayChange,
    effectiveVariant,
    driver,
  ]);

  const enterOverrideMode = useCallback(() => {
    setCurrentMode({ mode: 'override' });
  }, []);

  const exitOverrideMode = useCallback(() => {
    setStagedOverrides(undefined);
    setPreviewRewardOutput(undefined);
    setIsLoadingPreview(false);
    setCurrentMode({ mode: 'default' });
  }, []);

  const [fieldTagCounts, setFieldTagCounts] = useState<Record<string, number>>(
    {},
  );
  const setFieldTagCount = useCallback((fieldKey: string, count: number) => {
    setFieldTagCounts((prev) => ({ ...prev, [fieldKey]: count }));
  }, []);

  const enterRecordTaggingMode = useCallback(
    (
      taggableField: BonusRecordTagging.TaggableFieldOutput,
      fieldType: BonusRecordTagging.FieldType,
    ) => {
      setCurrentMode({ mode: 'recordTagging', taggableField, fieldType });
    },
    [],
  );

  const exitRecordTaggingMode = useCallback(() => {
    setCurrentMode({ mode: 'default' });
  }, []);

  const enterAdditionalPaymentsMode = useCallback(
    (view: AdditionalPaymentsView) => {
      setCurrentMode({
        mode: 'additionalPayments',
        additionalPaymentsView: view,
      });
    },
    [],
  );

  const exitAdditionalPaymentsMode = useCallback(() => {
    setCurrentMode({ mode: 'default' });
  }, []);

  // Recognition payments are per record, so switching driver leaves the mode.
  // There's nothing staged worth guarding (unlike overrides).
  useEffect(() => {
    setCurrentMode((mode) =>
      mode.mode === 'additionalPayments' ? { mode: 'default' } : mode,
    );
  }, [driver]);

  const onOverridesConfirmed = useCallback(
    (note: string, reasonCodes: string[]): Promise<void> => {
      if (!bonusPeriod || !stagedOverrides || !actualRewardOutput) {
        return Promise.reject();
      }

      const bonusDatasetRecordId = actualRewardOutput.bonusDatasetRecordId;
      const intervalId = bonusPeriod.id;

      // The unified PUT is a full REPLACE with no server-side merge, so send the
      // complete desired state for every kind = persisted ⊕ staged.
      const state = buildOverrideState({
        persisted: overrides,
        persistedRewardOverrides: actualRewardOutput.rewardOverrides,
        stagedCriteria: stagedOverrides.criteria,
        stagedQualifier: stagedOverrides.qualifier,
        stagedRewardOverrides: stagedOverrides.rewardOverrides,
      });

      const run = async (): Promise<boolean> => {
        // A confirm that leaves no overrides at all can't go through the PUT
        // (it rejects an empty body) — route a full clear to DELETE, which also
        // removes the reason codes and rebuilds the baseline.
        if (!hasAnyOverride(state)) {
          const response = await deleteDriverOverrides({
            api,
            driver,
            bonusProgramId,
            intervalId,
            bonusDatasetRecordId,
          });
          return response.ok || response.status === 404;
        }

        const body = toOverridesRequest({
          state,
          comment: note,
          reasonCodes,
        });
        const response = await putDriverOverrides({
          api,
          driver,
          bonusProgramId,
          intervalId,
          bonusDatasetRecordId,
          body,
        });
        return response.ok;
      };

      return run().then((ok) => {
        if (!ok) {
          return;
        }
        setIsLoading(true);
        exitOverrideMode();
        setRefreshKey(getIdentifier());
        showToast('Overrides Updated');
        onOverridesSaved?.();
      });
    },
    [
      actualRewardOutput,
      api,
      bonusPeriod,
      bonusProgramId,
      driver,
      exitOverrideMode,
      overrides,
      showToast,
      stagedOverrides,
      onOverridesSaved,
    ],
  );

  const refreshBonusOverview = useCallback(() => {
    setRefreshKey(getIdentifier());
    onOverridesSaved?.();
  }, [onOverridesSaved]);

  const contextValue = useMemo((): BonusOverviewContextType => {
    if (isLoading || !bonusOverview) {
      return {
        isLoading: true,
        isLoadingPreview: true,
        bonusOverview: undefined,
        fieldTagCounts,
        setFieldTagCount,
        rewardOverrideOptions,
        enterOverrideMode,
        exitOverrideMode,
        currentMode,
        onOverridesConfirmed,
        enterRecordTaggingMode,
        exitRecordTaggingMode,
        refreshBonusOverview,
        enterAdditionalPaymentsMode,
        exitAdditionalPaymentsMode,
        bonusProgramId,
      };
    }

    return {
      isLoading,
      isLoadingPreview,
      bonusOverview,
      fieldTagCounts,
      setFieldTagCount,
      rewardOverrideOptions,
      enterOverrideMode,
      exitOverrideMode,
      exitRecordTaggingMode,
      enterRecordTaggingMode,
      currentMode,
      onOverridesConfirmed,
      refreshBonusOverview,
      enterAdditionalPaymentsMode,
      exitAdditionalPaymentsMode,
      bonusProgramId,
    };
  }, [
    isLoading,
    bonusOverview,
    isLoadingPreview,
    fieldTagCounts,
    setFieldTagCount,
    rewardOverrideOptions,
    enterOverrideMode,
    exitOverrideMode,
    exitRecordTaggingMode,
    enterRecordTaggingMode,
    currentMode,
    onOverridesConfirmed,
    refreshBonusOverview,
    enterAdditionalPaymentsMode,
    exitAdditionalPaymentsMode,
    bonusProgramId,
  ]);

  const getDriverRewardOutputAndOverrides = useCallback(
    async ({
      bonusPeriodId,
      driver,
      bonusProgramId,
      subPeriod,
    }: {
      bonusPeriodId: string;
      driver: string;
      bonusProgramId: string;
      subPeriod: Period | undefined;
    }): Promise<
      | {
          rewardOutput: BonusTypes.Rewards.AnyDriverRewardsOutput;
          overrides: BonusTypes.Overrides.Persisted | undefined;
          rewardOverrideOptions: RewardOverrideOptionsLookup;
        }
      | undefined
    > => {
      // Resolve the reward output — GET /rewards for ordinary programmes, or the
      // specific weekly Firestore record for sub-period programmes (see
      // resolveDriverRewardOutput). Everything downstream keys on the record's
      // bonusDatasetRecordId, so no override API needs a sub-period concept.
      const newRewardOutput = await resolveDriverRewardOutput({
        api,
        accountId,
        driver,
        bonusProgramId,
        bonusPeriodId,
        subPeriod,
      });

      // Without the reward output there is nothing to render.
      if (!newRewardOutput) {
        return;
      }
      const [overrideResponse, optionsResponse] = await Promise.all([
        getDriverOverrides({
          api,
          bonusDatasetRecordId: newRewardOutput.bonusDatasetRecordId,
        }),
        getRewardOverrideOptions({
          api,
          bonusDatasetRecordId: newRewardOutput.bonusDatasetRecordId,
        }),
      ]);

      // Persisted overrides are best-effort: a non-200 (e.g. a deprecated endpoint being
      // removed) must not block the overview — we simply show no persisted override badges.
      if (!overrideResponse.ok) {
        console.log(
          'Driver bonus: GET /overrides returned non-200; rendering without persisted overrides',
          overrideResponse.status,
        );
      }
      const overrides = overrideResponse.ok
        ? overrideResponse.data || undefined
        : undefined;

      // Reward-override options are best-effort too: on failure the reward-level control just
      // doesn't render (as if no line were overridable).
      const rewardOverrideOptionsLookup: RewardOverrideOptionsLookup =
        new Map();
      if (optionsResponse.ok && optionsResponse.data) {
        optionsResponse.data.forEach((entry) => {
          rewardOverrideOptionsLookup.set(entry.rewardName, entry);
        });
      } else {
        console.log(
          'Driver bonus: GET /reward_override_options returned non-200; reward-level overrides unavailable',
          optionsResponse.status,
        );
      }

      return {
        rewardOutput: newRewardOutput,
        overrides,
        rewardOverrideOptions: rewardOverrideOptionsLookup,
      };
    },
    [api, accountId],
  );

  // Get the actual reward and persisted overrides
  useEffect(() => {
    if (!bonusPeriod) {
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setActualRewardOutput(undefined);
    setOverrides(undefined);
    setPreviewRewardOutput(undefined);
    setRewardOverrideOptions(undefined);
    getDriverRewardOutputAndOverrides({
      bonusPeriodId: bonusPeriod.id,
      bonusProgramId,
      driver,
      subPeriod: selectedSubPeriod,
    }).then((newData) => {
      if (!isActive) {
        return;
      }
      if (newData) {
        setActualRewardOutput(newData.rewardOutput);
        setOverrides(newData.overrides);
        setRewardOverrideOptions(newData.rewardOverrideOptions);
      } else {
        // No reward record for this driver in the selected period (the rewards
        // request 400s / returns nothing). Let the host deselect + toast rather
        // than leaving the slideout stuck loading.
        onRewardsUnavailable?.();
      }
      // Always clear the spinner — previously it only cleared on success, so a
      // driver missing from the period left the slideout spinning forever.
      setIsLoading(false);
    });
    return () => {
      isActive = false;
    };
  }, [
    api,
    bonusProgramId,
    driver,
    bonusPeriod,
    selectedSubPeriod,
    getDriverRewardOutputAndOverrides,
    onRewardsUnavailable,
    refreshKey,
  ]);

  // Get the server-side preview for staged overrides — criteria, qualifier and reward-level.
  useEffect(() => {
    if (!bonusPeriod || !actualRewardOutput) {
      return;
    }

    const hasCriteria = (stagedOverrides?.criteria.length ?? 0) > 0;
    const hasQualifier = !!stagedOverrides?.qualifier;
    const hasStagedRewardChange =
      (stagedOverrides?.rewardOverrides?.length ?? 0) > 0;

    if (!hasCriteria && !hasQualifier && !hasStagedRewardChange) {
      // Nothing staged for the server to project.
      setPreviewRewardOutput(undefined);
      setIsLoadingPreview(false);
      return;
    }

    // PUT /preview is a dry run of the PUT /overrides REPLACE, so send the same complete state
    // the confirm would (persisted ⊕ staged, every kind) — a kind left out would preview as
    // cleared.
    const state = buildOverrideState({
      persisted: overrides,
      persistedRewardOverrides: actualRewardOutput.rewardOverrides,
      stagedCriteria: stagedOverrides?.criteria,
      stagedQualifier: stagedOverrides?.qualifier,
      stagedRewardOverrides: stagedOverrides?.rewardOverrides,
    });

    let isActive = true;
    setIsLoadingPreview(true);

    putBonusPreview({
      api,
      ...state,
      bonusDatasetRecordId: actualRewardOutput.bonusDatasetRecordId,
      bonusProgramId,
      driver,
      intervalId: bonusPeriod.id,
    }).then((response) => {
      if (!isActive) {
        return;
      }
      // Always clear the loading flag — otherwise a non-OK preview (e.g. a rejected empty
      // payload) would leave the confirm button disabled indefinitely.
      if (response.ok && response.data) {
        setPreviewRewardOutput(response.data);
      }
      setIsLoadingPreview(false);
    });

    return () => {
      isActive = false;
    };
  }, [
    api,
    bonusPeriod,
    bonusProgramId,
    driver,
    actualRewardOutput,
    overrides,
    stagedOverrides,
  ]);

  return (
    <BonusOverviewContext.Provider value={contextValue}>
      {children}
    </BonusOverviewContext.Provider>
  );
};

export default BonusOverviewProvider;
